import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck, KeyRound, ShieldOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [error, setError]           = useState('');
  const [emailForm, setEmailForm]   = useState({ email: '', password: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [showPwd, setShowPwd]       = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [tenantInactive, setTenantInactive] = useState(false);
  const [step, setStep]             = useState('credentials');
  const [tempToken, setTempToken]   = useState('');
  const [totpCode, setTotpCode]     = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const totpInputRef = useRef(null);

  useEffect(() => {
    if (user) navigate(user.role === 'manager' ? '/manager' : '/');
  }, [user, navigate]);

  // Respuesta de login (password o Google): mismo contrato que POST /auth/login.
  const handleLoginResponse = (data) => {
    if (data.requires_2fa) {
      setTempToken(data.temp_token);
      setStep('totp');
      return;
    }

    // Rol que exige 2FA y todavía no lo tiene: el token que llega solo sirve para
    // activarlo. PrivateRoute muestra la pantalla de activación en cualquier ruta.
    if (data.mfa_enrollment_required) {
      login(data.token, data.user, true);
      navigate('/');
      return;
    }

    login(data.token, data.user);
    navigate(data.user?.role === 'manager' ? '/manager' : '/');
  };

  // Vuelta de Google: llega un código de un solo uso (?gcode=) que se canjea acá.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcode = params.get('gcode');
    const err = params.get('error');

    if (gcode) {
      window.history.replaceState({}, '', '/login');
      setGoogleLoading(true);
      fetch(`${API_URL}/verify/oauth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: gcode }),
      })
        .then(async r => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(data.error || 'Error de autenticación con Google.');
          handleLoginResponse(data);
        })
        .catch(e => setError(e.message))
        .finally(() => setGoogleLoading(false));
      return;
    }

    if (err) {
      window.history.replaceState({}, '', '/login');
      const msgs = {
        wrong_domain:   'Esa cuenta de Google no pertenece a un dominio habilitado para este espacio.',
        not_registered: 'Esa cuenta de Google no está dada de alta en este espacio. Pedíselo a un administrador.',
        auth_failed:    'Error de autenticación con Google.',
        cancelled:      'Inicio de sesión cancelado.',
        disabled:       'Tu usuario fue desactivado. Contactá al administrador.',
        locked:         'Cuenta bloqueada temporalmente por intentos fallidos. Probá de nuevo en unos minutos.',
      };
      setError(msgs[err] || 'Error de autenticación.');
    }
  }, []); // eslint-disable-line

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/verify/oauth/url`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.url) throw new Error(data.error || 'No se pudo conectar con Google.');
      window.location.assign(data.url);
    } catch (e) {
      setError(e.message);
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (step === 'totp') setTimeout(() => totpInputRef.current?.focus(), 100);
  }, [step]);

  // Detectar si el espacio (tenant) está desactivado
  useEffect(() => {
    fetch(`${API_URL}/config/status`)
      .then(async r => {
        if (r.status === 403) {
          const d = await r.json().catch(() => ({}));
          if (d.tenant_inactive) setTenantInactive(true);
        }
      })
      .catch(() => {});
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Credenciales incorrectas.');
      handleLoginResponse(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    if (totpCode.replace(/\s/g, '').length < 6) {
      setError('Ingresá los 6 dígitos del código.');
      return;
    }
    setTotpLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/auth/2fa/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code: totpCode }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Código inválido.');
      login(data.token, data.user);
      navigate(data.user?.role === 'manager' ? '/manager' : '/');
    } catch (e) {
      setError(e.message);
      setTotpCode('');
    } finally {
      setTotpLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">

        {/* Marca */}
        <div className="login-brand">
          <img src="/nodward-icon.png" alt="Nodward" width={44} height={44} style={{ display: 'block' }} />
          <span className="login-product"><strong style={{ fontWeight: 800, color: 'var(--accent)' }}>Nod</strong><span style={{ fontWeight: 400, color: 'var(--ink)' }}>ward</span></span>
        </div>

        <div className="login-divider" />

        {/* ── Espacio desactivado ── */}
        {tenantInactive && (
          <div style={{ textAlign: 'center', padding: '8px 4px 4px' }}>
            <ShieldOff size={40} style={{ color: '#a8a29e', marginBottom: 12 }} />
            <div className="login-title" style={{ textAlign: 'center' }}>Espacio desactivado</div>
            <div className="login-sub" style={{ textAlign: 'center' }}>
              Este espacio de trabajo está desactivado. Contactá al administrador para más información.
            </div>
          </div>
        )}

        {/* ── Paso 1: credenciales ── */}
        {!tenantInactive && step === 'credentials' && (
          <>
            <div className="login-title">Bienvenido</div>
            <div className="login-sub">Iniciá sesión con tu cuenta corporativa para continuar</div>

            {error && <div className="login-error">{error}</div>}

            <form className="login-form" onSubmit={handleEmailLogin}>
              <div className="login-field">
                <label className="login-label">Email</label>
                <input
                  className="login-input"
                  type="email"
                  placeholder="usuario@empresa.com"
                  required
                  autoComplete="email"
                  value={emailForm.email}
                  onChange={e => setEmailForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>

              <div className="login-field">
                <label className="login-label">Contraseña</label>
                <div className="login-input-wrap">
                  <input
                    className="login-input"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    value={emailForm.password}
                    onChange={e => setEmailForm(f => ({ ...f, password: e.target.value }))}
                    style={{ paddingRight: 36 }}
                  />
                  <button
                    type="button"
                    className="login-eye"
                    onClick={() => setShowPwd(v => !v)}
                    aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button className="login-submit" type="submit" disabled={emailLoading}>
                {emailLoading && <span className="spinner-sm" />}
                {emailLoading ? 'Verificando...' : 'Ingresar'}
              </button>
            </form>

            <Link to="/forgot-password" className="login-forgot">
              ¿Olvidaste tu contraseña?
            </Link>

            <div className="login-or">o</div>

            <button type="button" className="login-google" onClick={handleGoogleLogin} disabled={googleLoading}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {googleLoading ? 'Conectando con Google...' : 'Continuar con Google'}
            </button>
          </>
        )}

        {/* ── Paso 2: TOTP ── */}
        {!tenantInactive && step === 'totp' && (
          <>
            <div className="login-totp-icon"><KeyRound size={30} /></div>
            <div className="login-title" style={{ textAlign: 'center' }}>Verificación en dos pasos</div>
            <div className="login-sub" style={{ textAlign: 'center' }}>
              Ingresá el código de 6 dígitos de tu app autenticadora
            </div>

            {error && <div className="login-error">{error}</div>}

            <form className="login-form" onSubmit={handleTotpSubmit}>
              <input
                ref={totpInputRef}
                className="login-input login-totp-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9 ]*"
                maxLength={7}
                placeholder="000 000"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/[^0-9 ]/g, ''))}
              />
              <button className="login-submit" type="submit" disabled={totpLoading}>
                {totpLoading && <span className="spinner-sm" />}
                {totpLoading ? 'Verificando...' : 'Verificar código'}
              </button>
              <button
                type="button"
                className="login-back"
                onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); }}
              >
                ← Volver a las credenciales
              </button>
            </form>

            <div className="login-totp-hint">
              El código es válido por 30 segundos. Si expiró, esperá que la app genere uno nuevo.
            </div>
          </>
        )}

        <div className="login-notice">
          <ShieldCheck size={16} className="login-notice-icon" />
          <span>Acceso exclusivo para personal autorizado. Los intentos de acceso quedan registrados.</span>
        </div>

        <div className="login-footer">
          <div>nodward.com</div>
          <div className="login-legal">
            © {new Date().getFullYear()} Nodward. Todos los derechos reservados. Software protegido por la Ley N.º 11.723
            de Propiedad Intelectual (Argentina) y tratados internacionales. Prohibida su reproducción, distribución,
            modificación o ingeniería inversa sin autorización expresa del titular de los derechos.
          </div>
        </div>
      </div>
    </div>
  );
}
